/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
    IIssueChartDataChild,
    IIssueResponseBase,
    IssueActivityChartDataResponse,
    IssueActivityFilesAssignmentRequest,
    IssueActivityResponseUnion,
    IssueActivitySavingsApprovedResponse,
    IssueActivityUserChild,
    IssueAlertsUpdateRequest,
    IssueArchiveRequest,
    IssueChartDataFaultStatus,
    IssueChartDataUpdateRequest,
    IssueCommentCreateRequest,
    IssueCommentUpdateRequest,
    IssueDescriptionUpdateRequest,
    IssueFaultUpdateRequest,
    IssueFilesAssignmentMode,
    IssueFlagUpdateRequest,
    IssueHardwareHealthResponse,
    IssueHealthRangeCreateRequest,
    IssueHealthRangeUpdateRequest,
    IssueInternalUpdateRequest,
    IssueMachineHealthResponse,
    IssuePriorityUpdateRequest,
    IssueRecommendedActionUpdateRequest,
    IssueRemoveRequest,
    IssueResolvedDateUpdateRequest,
    IssueSavingsUpdateApplicableRequest,
    IssueSavingsUpdateRequest,
    IssueSeverityUpdateRequest,
    IssueStatusType,
    IssueStatusUpdateRequest,
    SavingsApprovalUpdateRequest,
} from '@kcftech/prometheus-client';
import { createContentPath } from '@kcftech/prometheus-files-sdk';
import { useMutation } from '@tanstack/react-query';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { sdClient, sdFilesClient } from 'contexts/SDKClientContext';
import { IssueChartDataInfo } from 'pages/Desk/issues/chart_data/types';
import {
    IssueEvent,
    useTriggerIssueEvent,
} from 'pages/Desk/issues/issue_details/IssueEventsProvider';
import {
    useAutoAdvanceQueue,
    useDeskQueueState,
} from 'pages/Desk/issues/issue_details/useDraftIssueQueue';
import {
    getChartDataCreateRequest,
    getIssueChartDataAnnotation,
} from 'pages/Desk/issues/issue_modals/shared';
import { useIssueStatuses } from 'pages/Desk/issues/useIssueStatuses';
import { ReactNode, useMemo } from 'react';
import { useHistory } from 'react-router';
import { ARCHIVED_ISSUE_STATUSES } from 'shared/issue_filters_context/types';
import { AnnotationConfig } from 'shared/svg_annotations/types';
import { getIssueDetailsRoute, getIssuesPageRoute } from 'utils/RouteHelper';
import { sentryCaptureException } from 'utils/sentry';
import { v4 as uuidv4 } from 'uuid';

export type CommentFile = Readonly<{
    id: string;
    file: File;
}>;

export type CreateCommentOptions = Readonly<{
    replyToCommentId?: string;
    comment?: string;
    files?: CommentFile[];
    libraryFileIds?: string[];
}>;

export type EditCommentOptions = Readonly<{
    activityId: string;
    comment?: string;
    filesToAdd?: CommentFile[];
    fileIdsToRemove?: string[];
}>;

export type UpdateChartDataOptions = Readonly<{
    chartDataId: string;
    title: string;
    annotations: AnnotationConfig[];
    faultStatus: IssueChartDataFaultStatus;
}>;

export type UpdateSavingsOptions = Readonly<{
    savingsRequest: IssueSavingsUpdateRequest;
    verifyRequest?: SavingsApprovalUpdateRequest;
}>;

export type ApproveSavingsOptions = Readonly<{
    savingsId: string;
    request: SavingsApprovalUpdateRequest;
}>;

export type UpdateHealthRangeOptions = Readonly<{
    healthRangeId: string;
    request: IssueHealthRangeUpdateRequest;
}>;

export type UpdateFlagOptions = Readonly<{
    newValue: boolean;
    shouldReceiveNotifications: boolean;
}>;

type OnSuccessOptions = Readonly<{
    content?: ReactNode;
    timeoutMs?: number;
    event?: IssueEvent;
}>;

export function useUpdateIssue(issue: IIssueResponseBase) {
    const { push } = useHistory();
    const { data } = useIssueStatuses();
    const { showToast } = useNotificationContext();
    const triggerIssueEvent = useTriggerIssueEvent();

    const { queuedIssueIds, setQueuedIssueIds } = useDeskQueueState();

    const autoAdvanceQueue = useAutoAdvanceQueue(
        issue,
        queuedIssueIds,
        setQueuedIssueIds,
        push,
    );

    const {
        issue: { canIndicateFaultOnChartData },
    } = usePermissions();

    const newIssueStatus = useMemo(
        () => data?.Result.find(({ StatusType }) => StatusType === IssueStatusType.New),
        [data?.Result],
    );

    const onError = (content: string) => {
        showToast({ type: 'error', content });
    };

    const onSuccess = ({ content, event, timeoutMs }: OnSuccessOptions) => {
        if (content) {
            showToast({ type: 'success', content, timeoutMs });
        }
        if (event) {
            triggerIssueEvent(event);
        }
    };

    const { mutate: updateStatus, isPending: isUpdatingStatus } = useMutation({
        meta: { name: 'update-issue-status' },
        mutationFn: (statusId: string) =>
            sdClient.updateIssueStatus({
                id: issue.Id,
                body: new IssueStatusUpdateRequest({ StatusId: statusId }),
            }),

        onError: () => onError('Failed to update issue status'),

        onSuccess: (activity) =>
            onSuccess({
                event: {
                    type: 'activity',
                    action: 'create',
                    activityId: activity.Id,
                    activity,
                },
            }),
    });

    const { mutate: updateSeverity, isPending: isUpdatingSeverity } = useMutation({
        meta: { name: 'update-issue-severity' },
        mutationFn: (severity: number) =>
            sdClient.updateIssueSeverity({
                id: issue.Id,
                body: new IssueSeverityUpdateRequest({ Severity: severity }),
            }),

        onError: () => onError('Failed to update issue severity'),

        onSuccess: (activity) =>
            onSuccess({
                event: {
                    type: 'activity',
                    action: 'create',
                    activityId: activity.Id,
                    activity,
                },
            }),
    });

    const { mutate: updatePriority, isPending: isUpdatingPriority } = useMutation({
        meta: { name: 'update-issue-priority' },
        mutationFn: (priority: number) =>
            sdClient.updateIssuePriority({
                id: issue.Id,
                body: new IssuePriorityUpdateRequest({ Priority: priority }),
            }),

        onError: () => onError('Failed to update issue priority'),

        onSuccess: (activity) =>
            onSuccess({
                event: {
                    type: 'activity',
                    action: 'create',
                    activityId: activity.Id,
                    activity,
                },
            }),
    });

    const { mutate: updateFaultTypes, isPending: isUpdatingFaultTypes } = useMutation({
        meta: { name: 'update-faults' },
        mutationFn: (faultTypeIds: string[]) =>
            sdClient.updateFaults({
                id: issue.Id,
                body: new IssueFaultUpdateRequest({ FaultTypeIds: faultTypeIds }),
            }),

        onError: () => onError('Failed to update issue fault types'),

        onSuccess: (activity) =>
            onSuccess({
                event: {
                    type: 'activity',
                    action: 'create',
                    activityId: activity.Id,
                    activity,
                },
            }),
    });

    const { mutate: updateAlerts, isPending: isUpdatingAlerts } = useMutation({
        meta: { name: 'update-issue-alerts' },
        mutationFn: (alertIds: string[]) =>
            sdClient.updateIssueAlerts({
                id: issue.Id,
                body: new IssueAlertsUpdateRequest({ AlertIds: alertIds }),
            }),

        onError: () => onError('Failed to update issue alerts'),

        // TODO: the explicit typing is because the API can return 204 in cases that
        // don't produce a updated alerts activity (ie an update that doesn't change the selected alerts)
        // This can be removed once the SDK understands that the request will possibly return undefined
        onSuccess: (activity: IssueActivityResponseUnion | null) =>
            onSuccess({
                event: activity
                    ? {
                          type: 'activity',
                          action: 'create',
                          activityId: activity.Id,
                          activity,
                      }
                    : undefined,
            }),
    });

    const { mutate: updateSavings, isPending: isUpdatingSavings } = useMutation({
        meta: { name: 'update-savings' },
        mutationFn: async ({ savingsRequest, verifyRequest }: UpdateSavingsOptions) => {
            const savingsActivity = await sdClient.updateIssueSavings({
                id: issue.Id,
                body: savingsRequest,
            });

            if (verifyRequest) {
                await sdClient.approveSavingsForIssue({
                    issueId: issue.Id,
                    body: verifyRequest,
                });
            }

            return savingsActivity;
        },

        onError: () => onError('Failed to update issue savings'),

        // TODO: the explicit typing is because the API can return 204 in cases that
        // don't produce a savings activity (ie updating just the savings value tags)
        // This can be removed once the SDK understands that the request will possibly return undefined
        onSuccess: (activity: IssueActivityResponseUnion | null) =>
            onSuccess({
                content: 'Successfully updated issue savings',
                event: activity
                    ? {
                          type: 'activity',
                          action: 'create',
                          activityId: activity.Id,
                          activity,
                      }
                    : undefined,
            }),
    });

    const {
        mutate: updateSavingsApplicability,
        isPending: isUpdatingSavingsApplicability,
    } = useMutation({
        meta: { name: 'update-issue-savings-applicability' },
        mutationFn: (request: IssueSavingsUpdateApplicableRequest) =>
            sdClient.updateIssueSavingsApplicability({ id: issue.Id, body: request }),

        onError: () => onError('Failed to update issue savings'),

        onSuccess: (activity) =>
            onSuccess({
                content: 'Successfully updated issue savings',
                event: {
                    type: 'activity',
                    action: 'create',
                    activityId: activity.Id,
                    activity,
                },
            }),
    });

    const { mutate: verifySavings, isPending: isVerifyingSavings } = useMutation({
        meta: { name: 'approve-savings' },
        mutationFn: ({ savingsId, request }: ApproveSavingsOptions) =>
            sdClient.approveSavings({ id: savingsId, body: request }),

        onError: () => onError('Failed to verify issue savings'),

        onSuccess: (activity) =>
            onSuccess({
                content: 'Successfully verified issue savings',
                event: {
                    type: 'activity',
                    action: 'create',
                    activityId: activity.Id,
                    activity: new IssueActivitySavingsApprovedResponse({
                        IssueId: issue.Id,
                        Created: activity.DateApproved!,
                        // TODO @jp: this _really_ needs idempotency key
                        Id: uuidv4(),
                        IsDraftActivity: false,
                        IssueActivityDate: activity.DateApproved!,
                        Updated: activity.DateApproved!,
                        Comment: undefined,
                        CreatedBy: new IssueActivityUserChild({
                            ApplicationRole: activity.ApprovedBy!.UserApplicationRole,
                            UserId: activity.ApprovedBy!.UserId,
                            UserEmail: activity.ApprovedBy!.UserEmail,
                            UserFirstName: activity.ApprovedBy!.UserFirstName,
                            UserLastName: activity.ApprovedBy!.UserLastName,
                        }),
                        IsEdited: false,
                    }),
                },
            }),
    });

    const { mutate: updateDescription, isPending: isUpdatingDescription } = useMutation({
        meta: { name: 'update-issue-description' },
        mutationFn: async (request: IssueDescriptionUpdateRequest) =>
            sdClient.updateIssueDescription({ id: issue.Id, body: request }),

        onError: () => onError('Failed to update issue description'),

        onSuccess: (activity) =>
            onSuccess({
                content: 'Successfully updated issue description',
                event: {
                    type: 'activity',
                    action: 'create',
                    activityId: activity.Id,
                    activity,
                },
            }),
    });

    const {
        mutate: updateRecommendation,
        isPending: isUpdatingRecommendation,
    } = useMutation({
        meta: { name: 'update-issue-recommended-action' },
        mutationFn: async (request: IssueRecommendedActionUpdateRequest) =>
            sdClient.updateIssueRecommendedAction({ id: issue.Id, body: request }),

        onError: () => onError('Failed to update issue recommendation'),

        onSuccess: (activity) =>
            onSuccess({
                event: {
                    type: 'activity',
                    action: 'create',
                    activityId: activity.Id,
                    activity,
                },
            }),
    });

    const { mutate: updateResolvedDate, isPending: isUpdatingResolvedDate } = useMutation(
        {
            meta: { name: 'update-issue-resolved-date' },
            mutationFn: async (resolvedDate: Date | undefined) =>
                await sdClient.updateIssueResolvedDate({
                    id: issue.Id,
                    body: new IssueResolvedDateUpdateRequest({
                        DateResolved: resolvedDate,
                    }),
                }),

            onError: () => onError('Failed to update issue resolved date'),

            onSuccess: (activity) =>
                onSuccess({
                    event: {
                        type: 'activity',
                        action: 'create',
                        activityId: activity.Id,
                        activity,
                    },
                }),
        },
    );

    const { mutate: saveNewChartData, isPending: isSavingNewChartData } = useMutation({
        meta: { name: 'save-new-chart-data' },
        mutationFn: async (chartData: IssueChartDataInfo[]) =>
            issue.IssueStatus.StatusType === IssueStatusType.Draft
                ? sdClient.addInitialChartData({
                      id: issue.Id,
                      body: chartData.map((info) =>
                          getChartDataCreateRequest(info, canIndicateFaultOnChartData),
                      ),
                  })
                : sdClient.createIssueChartData({
                      id: issue.Id,
                      body: chartData.map((info) =>
                          getChartDataCreateRequest(info, canIndicateFaultOnChartData),
                      ),
                  }),

        onError: (error) => {
            onError('Failed to add issue plot');
            sentryCaptureException({
                error,
                tags: { location: 'useUpdateIssue.saveNewChartData' },
            });
        },

        onSuccess: (result) =>
            onSuccess({
                event:
                    result instanceof IssueActivityChartDataResponse
                        ? {
                              type: 'activity',
                              action: 'create',
                              activityId: result.Id,
                              activity: result,
                          }
                        : { type: 'issue', action: 'update', issueId: issue.Id },
            }),
    });

    const { mutate: updateChartData, isPending: isUpdatingChartData } = useMutation({
        meta: { name: 'update-chart-data' },
        mutationFn: async (options: UpdateChartDataOptions) => {
            if (issue instanceof IssueHardwareHealthResponse) {
                throw new Error(
                    `Attempted to update chart data for a hardware issue, issue id: ${issue.Id}`,
                );
            }

            const request = new IssueChartDataUpdateRequest({
                Title: options.title,
                ChartAnnotations: options.annotations.map(getIssueChartDataAnnotation),
                FaultStatus: canIndicateFaultOnChartData
                    ? options.faultStatus
                    : undefined,
            });

            const isInitialChartData =
                issue instanceof IssueMachineHealthResponse &&
                issue.ChartDataIds.includes(options.chartDataId);

            return isInitialChartData
                ? sdClient.updateInitialChartData({
                      id: issue.Id,
                      chartDataId: options.chartDataId,
                      body: request,
                  })
                : sdClient.updateIssueChartData({
                      id: issue.Id,
                      chartDataId: options.chartDataId,
                      body: request,
                  });
        },

        onError: () => onError('Failed to updated issue plot'),

        onSuccess: (result) =>
            onSuccess({
                content: 'Successfully updated issue plot',
                event:
                    result instanceof IssueActivityChartDataResponse
                        ? {
                              type: 'activity',
                              action: 'update',
                              activityId: result.Id,
                              activity: result,
                          }
                        : { type: 'issue', action: 'update', issueId: issue.Id },
            }),
    });

    const { mutate: deleteChartData, isPending: isDeletingChartData } = useMutation({
        meta: { name: 'delete-issue-chart-data' },
        mutationFn: (chartData: IIssueChartDataChild) =>
            sdClient.deleteIssueChartData({ id: issue.Id, chartDataId: chartData.Id }),

        onSuccess: () => {
            onSuccess({
                content: 'Successfully deleted issue plot',
            });
            // TODO: find a better way to handle when chart data id deleted
            window.location.reload();
        },

        onError: () => onError('Failed to delete issue plot'),
    });

    const { mutate: publishDraftIssue, isPending: isPublishingDraftIssue } = useMutation({
        meta: { name: 'update-issue-status' },
        mutationFn: (autoCreateMaintenanceTask?: boolean) => {
            if (!newIssueStatus) {
                throw new Error('Missing IssueStatusType.New');
            }
            return sdClient.updateIssueStatus({
                id: issue.Id,
                body: new IssueStatusUpdateRequest({
                    StatusId: newIssueStatus.Id,
                    AutoCreateMaintenanceTaskForPublish: autoCreateMaintenanceTask,
                }),
            });
        },

        onError: () => onError('Failed to publish Draft issue'),

        onSuccess: (activity) => {
            onSuccess({
                content: 'Your Draft issue has been published as a New issue',
                event: {
                    type: 'activity',
                    action: 'create',
                    activityId: activity.Id,
                    activity,
                },
            });

            if (queuedIssueIds.length === 0 || !queuedIssueIds.includes(issue.Id)) {
                push(getIssueDetailsRoute(issue.Location.LocationId, issue.Id));
            } else {
                autoAdvanceQueue();
            }
        },
    });

    const { mutate: removeDraftIssue, isPending: isRemovingDraftIssue } = useMutation({
        meta: { name: 'remove-issue' },
        mutationFn: (request: IssueRemoveRequest) =>
            sdClient.removeIssue({ id: issue.Id, body: request }),

        onError: () => onError('Failed to delete Draft issue'),

        onSuccess: () => {
            onSuccess({
                content: 'Your Draft issue has been deleted',
            });

            if (queuedIssueIds.length === 0 || !queuedIssueIds.includes(issue.Id)) {
                const issueMode =
                    issue instanceof IssueMachineHealthResponse
                        ? 'machineHealth'
                        : 'hardwareHealth';
                push(getIssuesPageRoute(issue.Location.LocationId, issueMode, 'draft'));
            } else {
                autoAdvanceQueue();
            }
        },
    });

    const { mutate: createComment, isPending: isCreatingComment } = useMutation({
        meta: { name: 'create-issue-comment' },
        mutationFn: async (options: CreateCommentOptions) => {
            const fileUploads = options.files?.map((commentFile) => {
                const direntId = uuidv4();
                return sdFilesClient.writeFile(
                    createContentPath({
                        scopeType: 'location',
                        scopeId: issue.Location.LocationId,
                        filePath: `/desk/issue/${issue.FormattedIssueNumber}/comments/${commentFile.file.name}`,
                    }),
                    {
                        DirentId: direntId,
                        GroupIds: [],
                        MakeDirectories: true,
                        MediaType: commentFile.file.type,
                        Description: 'an issue comment file',
                        FileType: commentFile.file.type.startsWith('image/')
                            ? 'Picture'
                            : commentFile.file.type.startsWith('video/')
                              ? 'Video'
                              : 'GenericFile',
                        AutoRename: true,
                    },
                    commentFile.file,
                );
            });

            const createdFileIds = await Promise.all(fileUploads ?? []);

            return sdClient.createIssueComment({
                id: issue.Id,
                body: new IssueCommentCreateRequest({
                    Comment: options.comment,
                    DirectoryEntryIds: [
                        ...createdFileIds,
                        ...(options.libraryFileIds ?? []),
                    ],
                    ReplyToCommentId: options.replyToCommentId,
                }),
            });
        },

        onError: () => {
            onError('Failed to add comment');
        },

        onSuccess: (activity) =>
            onSuccess({
                event: {
                    type: 'activity',
                    action: 'create',
                    activityId: activity.Id,
                    activity,
                },
            }),
    });

    const { mutate: updateComment, isPending: isUpdatingComment } = useMutation({
        meta: { name: 'update-issue-comment' },
        mutationFn: async (options: EditCommentOptions) => {
            const filesChanges: IssueActivityFilesAssignmentRequest[] = [];

            if (options.filesToAdd?.length) {
                const fileUploads = options.filesToAdd.map((commentFile) => {
                    const direntId = uuidv4();
                    return sdFilesClient.writeFile(
                        createContentPath({
                            scopeType: 'location',
                            scopeId: issue.Location.LocationId,
                            filePath: `/desk/issue/${issue.FormattedIssueNumber}/comments/${commentFile.file.name}`,
                        }),
                        {
                            DirentId: direntId,
                            GroupIds: [],
                            MakeDirectories: true,
                            MediaType: commentFile.file.type,
                            Description: 'an issue comment file',
                            FileType: commentFile.file.type.startsWith('image/')
                                ? 'Picture'
                                : commentFile.file.type.startsWith('video/')
                                  ? 'Video'
                                  : 'GenericFile',
                            AutoRename: true,
                        },
                        commentFile.file,
                    );
                });

                const createdFileIds = await Promise.all(fileUploads);

                filesChanges.push(
                    new IssueActivityFilesAssignmentRequest({
                        Mode: IssueFilesAssignmentMode.Append,
                        DirectoryEntryIds: createdFileIds,
                    }),
                );
            }

            if (options.fileIdsToRemove?.length) {
                filesChanges.push(
                    new IssueActivityFilesAssignmentRequest({
                        Mode: IssueFilesAssignmentMode.Remove,
                        DirectoryEntryIds: options.fileIdsToRemove,
                    }),
                );
            }

            return sdClient.updateIssueComment({
                id: issue.Id,
                activityId: options.activityId,
                body: new IssueCommentUpdateRequest({
                    Comment: options.comment,
                    FilesChanges: filesChanges,
                }),
            });
        },

        onError: () => onError('Failed to update comment'),

        onSuccess: (activity) =>
            onSuccess({
                event: {
                    type: 'activity',
                    action: 'update',
                    activityId: activity.Id,
                    activity,
                },
            }),
    });

    const { mutate: deleteComment, isPending: isDeletingComment } = useMutation({
        meta: { name: 'delete-issue-comment' },
        mutationFn: async ({
            id: commentId,
            deleteTriggeringComment,
        }: {
            id: string;
            deleteTriggeringComment?: boolean;
        }) =>
            sdClient.deleteIssueComment({
                id: issue.Id,
                activityId: commentId,
                deleteTriggeringCommentIfApplicable: deleteTriggeringComment,
            }),

        onError: () => onError('Failed to delete comment'),

        onSuccess: (activity) =>
            onSuccess({
                event: {
                    type: 'activity',
                    action: 'create',
                    activityId: activity.Id,
                    activity,
                },
            }),
    });

    const { mutate: archiveIssue, isPending: isArchivingIssue } = useMutation({
        meta: { name: 'archive-issue' },
        mutationFn: async (request: IssueArchiveRequest) =>
            sdClient.archiveIssue({ id: issue.Id, body: request }),

        onError: () => onError('Failed to update issue status'),

        onSuccess: () =>
            onSuccess({
                event: { type: 'issue', action: 'update', issueId: issue.Id },
            }),
    });

    const {
        mutate: updateFaultLocation,
        isPending: isUpdatingFaultLocation,
    } = useMutation({
        meta: { name: 'update-issue-group' },
        mutationFn: async (group: string) => {
            await sdClient.updateIssueGroup({ id: issue.Id, body: group });
        },

        onSuccess: () => {
            onSuccess({
                content: 'Successfully updated fault location',
                event: { type: 'issue', action: 'update', issueId: issue.Id },
            });
        },

        onError: () => onError('Failed to update fault location'),
    });

    const { mutate: createHealthRange, isPending: isCreatingHealthRange } = useMutation({
        meta: { name: 'create-issue-health-range' },
        mutationFn: (request: IssueHealthRangeCreateRequest) =>
            sdClient.createIssueHealthRange({
                id: issue.Id,
                body: request,
            }),

        onSuccess: () =>
            onSuccess({
                content: 'Successfully created issue health range',
            }),

        onError: () => onError('Failed to create issue health range'),
    });

    const { mutate: updateHealthRange, isPending: isUpdatingHealthRange } = useMutation({
        meta: { name: 'update-issue-health-range' },
        mutationFn: ({ healthRangeId, request }: UpdateHealthRangeOptions) =>
            sdClient.updateIssueHealthRange({ id: healthRangeId, body: request }),

        onSuccess: () =>
            onSuccess({
                content: 'Successfully updated issue health range',
            }),

        onError: () => onError('Failed to update issue health range'),
    });

    const { mutate: deleteHealthRange, isPending: isDeletingHealthRange } = useMutation({
        meta: { name: 'delete-issue-health-range' },
        mutationFn: (healthRangeId: string) =>
            sdClient.deleteIssueHealthRange({ id: healthRangeId }),
        onSuccess: () =>
            onSuccess({
                content: 'Successfully deleted health range',
            }),

        onError: () => onError('Failed to delete health range'),
    });

    const {
        mutate: updateHardwareHealthIssueIsInternal,
        isPending: isUpdatingHardwareHealthIssueIsInternal,
    } = useMutation({
        meta: { name: 'update-hardware-health-internal' },
        mutationFn: (newValue: boolean) =>
            sdClient.updateHardwareHealthInternal({
                id: issue.Id,
                body: new IssueInternalUpdateRequest({ IsInternal: newValue }),
            }),

        onSuccess: () =>
            onSuccess({
                content: 'Successfully updated KCF Only status',
                event: { type: 'issue', action: 'update', issueId: issue.Id },
            }),

        onError: () => onError('Failed to update KCF Only status'),
    });

    const {
        mutate: updateIssueIsFlagged,
        isPending: isUpdatingIssueIsFlagged,
    } = useMutation({
        meta: { name: 'unflag-issue' },
        mutationFn: async ({
            newValue,
            shouldReceiveNotifications,
        }: UpdateFlagOptions) => {
            const isArchived = ARCHIVED_ISSUE_STATUSES.includes(
                issue.IssueStatus.StatusType,
            );

            if (newValue) {
                await sdClient.flagIssue({
                    id: issue.Id,
                    body: new IssueFlagUpdateRequest({
                        ShouldReceiveNotifications: shouldReceiveNotifications,
                    }),
                });
                return { flagged: true, shouldReceiveNotifications, isArchived };
            }
            await sdClient.unflagIssue({ id: issue.Id });
            return { flagged: false, shouldReceiveNotifications: false, isArchived };
        },

        onSuccess: (result) =>
            onSuccess({
                event: {
                    type: 'flag',
                    action: 'update',
                    issueId: issue.Id,
                    flagInfo: result,
                },
            }),
        onError: () => onError('Failed to update issue flagged status'),
    });

    return {
        updateRecommendation,
        updateDescription,
        updateStatus,
        updateSeverity,
        updatePriority,
        updateFaultTypes,
        updateAlerts,
        updateSavings,
        updateSavingsApplicability,
        verifySavings,
        saveNewChartData,
        publishDraftIssue,
        removeDraftIssue,
        createComment,
        updateComment,
        deleteComment,
        updateChartData,
        deleteChartData,
        archiveIssue,
        updateFaultLocation,
        createHealthRange,
        updateHealthRange,
        deleteHealthRange,
        updateHardwareHealthIssueIsInternal,
        updateIssueIsFlagged,
        updateResolvedDate,
        isUpdatingRecommendation,
        isUpdatingDescription,
        isUpdatingStatus,
        isUpdatingSeverity,
        isUpdatingPriority,
        isUpdatingFaultTypes,
        isUpdatingAlerts,
        isUpdatingSavings,
        isUpdatingSavingsApplicability,
        isVerifyingSavings,
        isSavingNewChartData,
        isPublishingDraftIssue,
        isRemovingDraftIssue,
        isCreatingComment,
        isUpdatingComment,
        isDeletingComment,
        isUpdatingChartData,
        isDeletingChartData,
        isArchivingIssue,
        isUpdatingFaultLocation,
        isCreatingHealthRange,
        isUpdatingHealthRange,
        isDeletingHealthRange,
        isUpdatingHardwareHealthIssueIsInternal,
        isUpdatingIssueIsFlagged,
        isUpdatingResolvedDate,
    };
}
